<template>
  <div>
    <Head title="Cajas Registradoras" />

    <!-- Header -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
      <div class="flex items-center gap-4">
        <button 
          class="p-3 text-white bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors"
          title="Volver atrás"
          @click="back()"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="text-3xl font-bold">Gestión de Cajas</h1>
          <p class="text-xs text-gray-500 mt-1">Aperturas, cierres y reportes globales por tienda</p>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex flex-wrap items-center gap-3">
        <!-- Global Report Button -->
        <button 
          @click="openGlobalReportModal"
          class="px-4 py-2.5 bg-gray-800 text-white font-semibold rounded-lg hover:bg-gray-900 transition-colors flex items-center gap-2 shadow-sm text-sm"
        >
          <i class="fa fa-file-pdf text-amber-400"></i>
          <span>Reporte Global por Tienda</span>
        </button>

        <!-- Action for Admin/Financial -->
        <button 
          v-if="['admin', 'financial'].includes(role)"
          @click="openModal"
          class="px-4 py-2.5 bg-teal-700 text-white font-semibold rounded-lg hover:bg-teal-800 transition-colors flex items-center gap-2 shadow-sm text-sm"
        >
          <i class="fa fa-plus-circle text-lg"></i>
          <span>Aperturar Caja</span>
        </button>
      </div>
    </div>

    <!-- Info banner for Sales without active register -->
    <div 
      v-if="role === 'sales' && !hasActiveCaja"
      class="mb-8 p-6 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg shadow-sm"
    >
      <div class="flex gap-4 items-start">
        <i class="fa fa-exclamation-triangle text-amber-500 text-2xl mt-1"></i>
        <div>
          <h3 class="text-lg font-bold text-amber-900">Sin caja abierta asignada</h3>
          <p class="text-amber-800 mt-1">
            Actualmente no posees una caja activa. Para registrar ventas o movimientos de caja, solicita a un administrador que te asigne y aperture una caja en tu tienda.
          </p>
        </div>
      </div>
    </div>

    <!-- Active Registers (Admin/Financial only) -->
    <div v-if="['admin', 'financial'].includes(role) && filteredActiveCajas.length > 0" class="mb-8">
      <h2 class="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
        <span>Cajas Abiertas Actualmente</span>
        <span class="text-xs bg-teal-100 text-teal-800 font-bold px-2 py-0.5 rounded-full">{{ filteredActiveCajas.length }}</span>
      </h2>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          v-for="caja in filteredActiveCajas" 
          :key="caja.id"
          class="bg-white border border-teal-100 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden flex flex-col justify-between"
        >
          <div class="absolute top-0 right-0 w-24 h-24 bg-teal-50 rounded-bl-full -z-10 flex items-start justify-end p-4">
            <span class="text-xs bg-teal-100 text-teal-800 font-bold px-2 py-0.5 rounded">Abierta</span>
          </div>
          
          <div>
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 rounded-full bg-teal-100 text-teal-800 flex items-center justify-center font-bold">
                {{ getUserInitials(caja.user) }}
              </div>
              <div>
                <h3 class="font-bold text-gray-900">{{ getUserName(caja.user) }}</h3>
                <p class="text-xs text-gray-500">{{ caja.user ? caja.user.email : '-' }}</p>
              </div>
            </div>

            <div class="mb-4">
              <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                <i class="fa fa-store"></i>
                <span>{{ caja.deposit ? caja.deposit.name : 'Sin Tienda' }}</span>
              </span>
            </div>

            <div class="space-y-2 text-sm border-t border-gray-100 pt-4 mb-6">
              <div class="flex justify-between">
                <span class="text-gray-500">Apertura:</span>
                <div class="text-right">
                  <div class="font-semibold text-gray-800">${{ formatPrice(caja.opening_amount) }}</div>
                  <div class="text-xs text-gray-500">Bs {{ formatPrice(caja.opening_amount_bs) }}</div>
                </div>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Aperturada por:</span>
                <span class="text-gray-700">{{ getUserName(caja.opened_by) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Fecha/Hora:</span>
                <span class="text-gray-600 text-xs mt-0.5">{{ formatDate(caja.opened_at) }}</span>
              </div>
            </div>
          </div>

          <div class="flex gap-3">
            <Link 
              :href="`/cajas/${caja.id}`"
              class="flex-1 text-center py-2 bg-teal-50 hover:bg-teal-100 text-teal-800 font-medium rounded-lg transition-colors text-sm"
            >
              Gestionar
            </Link>
            <a 
              :href="`/cajas/${caja.id}/report`" 
              target="_blank"
              class="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm flex items-center justify-center"
              title="Reporte Individual en PDF"
            >
              <i class="fa fa-file-pdf"></i>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- History / General List -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div class="p-6 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h2 class="text-xl font-bold text-gray-800">
          {{ ['admin', 'financial'].includes(role) ? 'Historial General de Cajas' : 'Mi Historial de Cajas' }}
        </h2>

        <!-- Filter by Deposit -->
        <div class="flex items-center gap-3">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Filtrar por Tienda:</label>
          <select 
            v-model="selectedDepositFilter"
            class="form-select rounded-lg border-gray-300 text-sm focus:border-teal-500 focus:ring-teal-500 min-w-[200px]"
          >
            <option value="">Todas las Tiendas</option>
            <option 
              v-for="dep in deposits" 
              :key="dep.id" 
              :value="dep.id"
            >
              {{ dep.name }}
            </option>
          </select>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left bg-gray-50 text-gray-500 text-xs font-semibold uppercase tracking-wider">
              <th class="py-4 px-6">Vendedor / Cajero</th>
              <th class="py-4 px-6">Tienda / Depósito</th>
              <th class="py-4 px-6 text-right">Apertura</th>
              <th class="py-4 px-6 text-right">Cierre Real</th>
              <th class="py-4 px-6 text-right">Esperado</th>
              <th class="py-4 px-6 text-center">Estado</th>
              <th class="py-4 px-6">Apertura</th>
              <th class="py-4 px-6">Cierre</th>
              <th class="py-4 px-6 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 text-sm">
            <tr 
              v-for="caja in displayCajas" 
              :key="caja.id"
              class="hover:bg-gray-50 transition-colors"
            >
              <td class="py-4 px-6">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center font-bold text-xs">
                    {{ getUserInitials(caja.user) }}
                  </div>
                  <div>
                    <div class="font-medium text-gray-900">{{ getUserName(caja.user) }}</div>
                    <div class="text-xs text-gray-500">{{ caja.user ? caja.user.email : '-' }}</div>
                  </div>
                </div>
              </td>
              <td class="py-4 px-6">
                <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                  <i class="fa fa-store text-gray-400"></i>
                  <span>{{ caja.deposit ? caja.deposit.name : 'Sin Tienda' }}</span>
                </span>
              </td>
              <td class="py-4 px-6 text-right font-medium text-gray-900">
                <div>${{ formatPrice(caja.opening_amount) }}</div>
                <div class="text-xs text-gray-500">Bs {{ formatPrice(caja.opening_amount_bs) }}</div>
              </td>
              <td class="py-4 px-6 text-right font-medium text-gray-900">
                <div v-if="caja.closing_amount !== null">${{ formatPrice(caja.closing_amount) }}</div>
                <div v-if="caja.closing_amount_bs !== null" class="text-xs text-gray-500">Bs {{ formatPrice(caja.closing_amount_bs) }}</div>
                <span v-if="caja.closing_amount === null" class="text-gray-400">-</span>
              </td>
              <td class="py-4 px-6 text-right font-medium text-gray-900">
                <div v-if="caja.expected_amount !== null">${{ formatPrice(caja.expected_amount) }}</div>
                <div v-if="caja.expected_amount_bs !== null" class="text-xs text-gray-500">Bs {{ formatPrice(caja.expected_amount_bs) }}</div>
                <span v-if="caja.expected_amount === null" class="text-gray-400">-</span>
              </td>
              <td class="py-4 px-6 text-center">
                <span 
                  :class="[
                    'px-2.5 py-1 text-xs font-bold rounded-full',
                    caja.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  ]"
                >
                  {{ caja.status === 'open' ? 'Abierta' : 'Cerrada' }}
                </span>
              </td>
              <td class="py-4 px-6 text-gray-600">
                <div>{{ formatDate(caja.opened_at) }}</div>
                <div class="text-xs text-gray-400">por: {{ getUserName(caja.opened_by) }}</div>
              </td>
              <td class="py-4 px-6 text-gray-600">
                <div v-if="caja.closed_at">{{ formatDate(caja.closed_at) }}</div>
                <div v-if="caja.closed_by" class="text-xs text-gray-400">por: {{ getUserName(caja.closed_by) }}</div>
                <span v-else class="text-gray-400">-</span>
              </td>
              <td class="py-4 px-6 text-center">
                <div class="flex items-center justify-center gap-2">
                  <Link 
                    :href="`/cajas/${caja.id}`"
                    class="px-3 py-1.5 bg-gray-50 text-teal-800 hover:bg-teal-50 border border-gray-200 hover:border-teal-200 rounded-lg text-xs font-semibold transition-colors"
                  >
                    Detalle
                  </Link>
                  <a 
                    :href="`/cajas/${caja.id}/report`" 
                    target="_blank"
                    class="px-2 py-1.5 bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200 rounded-lg text-xs transition-colors"
                    title="Reporte Individual PDF"
                  >
                    <i class="fa fa-file-pdf"></i>
                  </a>
                </div>
              </td>
            </tr>

            <tr v-if="displayCajas.length === 0">
              <td colspan="9" class="text-center py-12 text-gray-500">
                No se encontraron registros de caja para el filtro seleccionado.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <pagination 
        v-if="cajas.links && !selectedDepositFilter"
        :links="cajas.links" 
        class="p-6 border-t border-gray-100"
      />
    </div>

    <!-- Aperturar Caja Modal (Admin only) -->
    <div 
      v-if="showOpenModal"
      class="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-fade-in-down">
        <div class="p-6 bg-teal-800 text-white flex justify-between items-center">
          <h3 class="text-lg font-bold">Aperturar Nueva Caja</h3>
          <button 
            @click="closeModal" 
            class="text-white hover:text-gray-200 transition-colors"
          >
            <i class="fa fa-times text-xl"></i>
          </button>
        </div>

        <form @submit.prevent="submitOpenCaja" class="p-6 space-y-4">
          <!-- Tienda / Deposito Selector -->
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Tienda / Depósito</label>
            <select 
              v-model="openForm.deposit_id" 
              required
              class="w-full form-select rounded-lg border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-sm"
            >
              <option value="" disabled>Seleccione una tienda...</option>
              <option 
                v-for="dep in deposits" 
                :key="dep.id" 
                :value="dep.id"
              >
                {{ dep.name }} ({{ dep.address || 'Sin dirección' }})
              </option>
            </select>
            <p v-if="openForm.errors.deposit_id" class="text-red-500 text-xs mt-1">{{ openForm.errors.deposit_id }}</p>
          </div>

          <!-- Vendedor Asignado Selector -->
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Vendedor / Cajero Asignado</label>
            <select 
              v-model="openForm.user_id" 
              required
              class="w-full form-select rounded-lg border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-sm"
            >
              <option value="" disabled>Seleccione un vendedor...</option>
              <option 
                v-for="vend in vendedores" 
                :key="vend.id" 
                :value="vend.id"
              >
                {{ getUserName(vend) }} ({{ vend.email }})
              </option>
            </select>
            <p v-if="openForm.errors.user_id" class="text-red-500 text-xs mt-1">{{ openForm.errors.user_id }}</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <text-input 
                v-model="openForm.opening_amount"
                type="number"
                step="0.01"
                label="Apertura (USD)"
                placeholder="Monto en USD"
                required
                :error="openForm.errors.opening_amount"
              />
            </div>
            <div>
              <text-input 
                v-model="openForm.opening_amount_bs"
                type="number"
                step="0.01"
                label="Apertura (Bs)"
                placeholder="Monto en Bs"
                required
                :error="openForm.errors.opening_amount_bs"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Observaciones / Notas</label>
            <textarea 
              v-model="openForm.notes"
              rows="3"
              class="w-full form-textarea rounded-lg border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-sm"
              placeholder="Notas u observaciones de apertura (opcional)..."
            ></textarea>
            <p v-if="openForm.errors.notes" class="text-red-500 text-xs mt-1">{{ openForm.errors.notes }}</p>
          </div>

          <div class="flex justify-end gap-3 border-t border-gray-100 pt-4">
            <button 
              type="button" 
              @click="closeModal"
              class="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium text-sm"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              :disabled="openForm.processing"
              class="px-4 py-2 bg-teal-800 hover:bg-teal-900 text-white rounded-lg transition-colors font-bold text-sm flex items-center gap-2"
            >
              <i v-if="openForm.processing" class="fa fa-spinner fa-spin"></i>
              <span>Abrir Caja</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Reporte Global por Tienda Modal -->
    <div 
      v-if="showGlobalReportModal"
      class="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-fade-in-down">
        <div class="p-6 bg-gray-800 text-white flex justify-between items-center">
          <div class="flex items-center gap-2">
            <i class="fa fa-file-pdf text-amber-400 text-xl"></i>
            <h3 class="text-lg font-bold">Reporte Global de Cajas</h3>
          </div>
          <button 
            @click="closeGlobalReportModal" 
            class="text-white hover:text-gray-200 transition-colors"
          >
            <i class="fa fa-times text-xl"></i>
          </button>
        </div>

        <form @submit.prevent="generateGlobalReport" class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Seleccionar Tienda / Depósito</label>
            <select 
              v-model="globalReportForm.deposit_id" 
              required
              class="w-full form-select rounded-lg border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-sm"
            >
              <option value="" disabled>Seleccione una tienda...</option>
              <option 
                v-for="dep in deposits" 
                :key="dep.id" 
                :value="dep.id"
              >
                {{ dep.name }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Fecha del Reporte</label>
            <input 
              v-model="globalReportForm.date"
              type="date"
              required
              class="w-full form-input rounded-lg border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-sm"
            />
          </div>

          <div class="flex justify-end gap-3 border-t border-gray-100 pt-4">
            <button 
              type="button" 
              @click="closeGlobalReportModal"
              class="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium text-sm"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              class="px-4 py-2 bg-teal-800 hover:bg-teal-900 text-white rounded-lg transition-colors font-bold text-sm flex items-center gap-2"
            >
              <i class="fa fa-print"></i>
              <span>Generar Reporte PDF</span>
            </button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import TextInput from '@/Shared/TextInput'

export default {
  name: 'CajasIndex',

  components: {
    Head,
    Link,
    Pagination,
    TextInput
  },

  layout: Layout,

  props: {
    vendedores: Array,
    cajas: Object,
    deposits: Array,
    role: String
  },

  data() {
    return {
      showOpenModal: false,
      showGlobalReportModal: false,
      selectedDepositFilter: '',
      openForm: this.$inertia.form({
        user_id: '',
        deposit_id: '',
        opening_amount: 0.0,
        opening_amount_bs: 0.0,
        notes: ''
      }),
      globalReportForm: {
        deposit_id: '',
        date: new Date().toISOString().split('T')[0]
      }
    }
  },

  computed: {
    activeCajas() {
      if (!['admin', 'financial'].includes(this.role)) return []
      return this.cajas.data.filter(c => c.status === 'open')
    },
    filteredActiveCajas() {
      if (!this.selectedDepositFilter) return this.activeCajas
      return this.activeCajas.filter(c => c.deposit_id == this.selectedDepositFilter)
    },
    displayCajas() {
      if (!this.selectedDepositFilter) return this.cajas.data
      return this.cajas.data.filter(c => c.deposit_id == this.selectedDepositFilter)
    },
    hasActiveCaja() {
      return this.cajas.data.some(c => c.status === 'open' && c.user_id === this.$page.props.auth.user.id)
    }
  },

  methods: {
    back() {
      window.history.back()
    },
    formatPrice(value) {
      return Number(value).toFixed(2)
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    openModal() {
      if (this.deposits && this.deposits.length > 0) {
        this.openForm.deposit_id = this.deposits[0].id
      }
      this.showOpenModal = true
    },
    closeModal() {
      this.showOpenModal = false
      this.openForm.reset()
    },
    openGlobalReportModal() {
      if (this.deposits && this.deposits.length > 0) {
        this.globalReportForm.deposit_id = this.deposits[0].id
      }
      this.showGlobalReportModal = true
    },
    closeGlobalReportModal() {
      this.showGlobalReportModal = false
    },
    generateGlobalReport() {
      if (!this.globalReportForm.deposit_id) return
      const url = `/cajas/report-global?deposit_id=${this.globalReportForm.deposit_id}&date=${this.globalReportForm.date}`
      window.open(url, '_blank')
      this.closeGlobalReportModal()
    },
    submitOpenCaja() {
      this.openForm.post('/cajas', {
        onSuccess: () => {
          this.closeModal()
          this.$toast.success('Caja aperturada con éxito.')
        },
        onError: (err) => {
          const errMsg = err.error || 'Ocurrió un error al aperturar la caja.'
          this.$toast.error(errMsg)
        }
      })
    },
    getUserName(user) {
      if (!user) return '-'
      if (user.first_name || user.last_name) {
        return `${user.first_name || ''} ${user.last_name || ''}`.trim()
      }
      return user.name || '-'
    },
    getUserInitials(user) {
      if (!user) return '?'
      const name = this.getUserName(user)
      return name.charAt(0).toUpperCase()
    }
  }
}
</script>

<style scoped>
.animate-fade-in-down {
  animation: fadeInDown 0.2s ease-out;
}
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>

